import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { mutate } from "swr";

import { usePOST, NetworkError } from "hooks/usePublicAPI";
import { useFirebase } from "firebase";
import AuthLayout from "layouts/AuthLayout";
import Loading from "components/Loading";

type VerifyPayload = { token?: string; firebase_id_token?: string };
type VerifyResponse = {
  success: boolean;
  custom_token?: string;
  next_step?: string;
  error?: string;
};

export default function AuthVerifyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, signInWithCustomToken } = useFirebase();
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string>("Verifying...");

  const {
    trigger: triggerVerify,
    isMutating: isVerifying,
    error: verifyError,
  } = usePOST<VerifyResponse, NetworkError, VerifyPayload>("/auth/verify");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) {
      setError("Verification token is missing or invalid.");
      setStatusMessage("Error");
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await triggerVerify({ token });
        if (response?.success && response.custom_token) {
          setStatusMessage("Refreshing session...");
          try {
            await signInWithCustomToken(auth, response.custom_token);
            mutate("/me"); // Force refresh of user data

            if (response.next_step === "set_password") {
              navigate("/set-password", { replace: true });
            } else {
              navigate("/account", {
                replace: true,
                state: { message: "Verification successful!" }, // Pass success message
              });
            }
          } catch (signInError: any) {
            console.error("Error signing in with custom token:", signInError);
            Sentry.captureException(signInError);
            setError(
              "Verification succeeded, but failed to refresh session. Please log in again.",
            );
            setStatusMessage("Error");
          }
        } else {
          // Handle API returning success: false
          setError(
            response?.error ||
              "Verification failed. The link may be invalid or expired.",
          );
          setStatusMessage("Error");
        }
      } catch (err) {
        // Error handled by useEffect below, just log here if needed
        console.error("Error triggering /auth/verify:", err);
        // setError is handled by the effect watching verifyError
        setStatusMessage("Error");
      }
    };

    verifyToken();

    // Run only once on mount, dependencies are stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerVerify, signInWithCustomToken, auth, navigate, location.search]);

  // Handle errors from the usePOST hook
  useEffect(() => {
    if (verifyError) {
      console.error("Verification failed (usePOST):", verifyError);
      Sentry.captureException(verifyError);
      let friendlyMessage =
        "Verification failed. The link may be invalid or expired.";
      if (typeof verifyError === "object" && verifyError !== null) {
        if (
          "statusCode" in verifyError &&
          typeof (verifyError as any).statusCode === "number"
        ) {
          const netError = verifyError as NetworkError;
          friendlyMessage =
            netError.responseData?.error || netError.message || friendlyMessage;
        } else if (
          "message" in verifyError &&
          typeof (verifyError as any).message === "string"
        ) {
          friendlyMessage = (verifyError as Error).message;
        }
      }
      setError(friendlyMessage);
      setStatusMessage("Error");
    }
  }, [verifyError]);

  return (
    <AuthLayout
      title={statusMessage}
      description="Please wait while we complete the verification."
    >
      <div className="flex justify-center py-8">
        {
          isVerifying ||
          statusMessage === "Verifying..." ||
          statusMessage === "Refreshing session..." ? (
            <Loading message={statusMessage} />
          ) : error ? (
            <div className="rounded bg-red-100 p-4 text-center text-red-700">
              <p className="font-semibold">Verification Failed</p>
              <p className="text-sm">{error}</p>
              {/* Maybe link to login or support? */}
            </div>
          ) : null /* Should redirect on success */
        }
      </div>
    </AuthLayout>
  );
}
